import { ability, ACTIONS, Can, SUBJECTS } from '../../../Permission'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { ReactComponent as Plus16 } from '../../../../assets/svg/Plus16.svg'
import { BTN_TXT, PLACEHOLDERS } from '../../../../constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as Close16 } from '../../../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from '../../../../assets/svg/Search16.svg'
import { Input } from '../../../../components/Input'
import { setLoading } from '../../../../redux/store/common/slice'
import { useSelector } from 'react-redux'
import { getUser } from '../../../../redux/store/user/getters'
import { useAppDispatch } from '../../../../redux/createStore'
import { columns } from './columns'
import { Table } from '../../../../components/Table'
import {
  AddHealthSystemParams,
  AddHealthSystemRequestData
} from '../../../ContractCategoryDetails/Facilities/AddHealthSystemPopup/types'
import axios, { CancelTokenSource } from 'axios'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { THealthSystem } from './types'
import { fetchHealthSystemsList } from '../../../HealthSystems/api'
import './styles.scss'
import { useDebounce } from '../../../../hooks/hooks'
import { routes } from '../../../../router'
import { useNavigate } from 'react-router'

export const HealthSystems = () => {
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<THealthSystem[]>([])
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [sortParams, setSortParams] = useState<AddHealthSystemRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const navigate = useNavigate()

  const tableDataTokenRef = useRef<CancelTokenSource>()

  const user = useSelector(getUser)
  const dispatch = useAppDispatch()

  const debouncedSearch = useDebounce(searchValue, 500)

  const onAddHS = useCallback(() => {
    navigate(
      `${routes.myCommunity}${routes.myOrganization}/health_systems/new`,
      {
        replace: true
      }
    )
  }, [navigate])

  const getTableData = async ({
    sortField,
    sortOrder,
    page
  }: AddHealthSystemRequestData) => {
    try {
      dispatch(setLoading(true))
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: AddHealthSystemParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      const { data } = await fetchHealthSystemsList(user.community as string, {
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
      setTableData(data.results)
      setTotalNumber(data.count)
      if (firstLoad) {
        setFirstLoad(false)
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleTableChange = async (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    await getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = async (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    await getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }
  const canSeeActionColumn = useMemo(() => {
    return ability.can(
      ACTIONS.VIEW,
      SUBJECTS.MY_ORGANIZATION_COMMUNITY_HS_TABLE_ACTIONS
    )
  }, [])

  const tableColumns = useMemo(() => {
    return columns({
      userHS: user.health_system,
      canSeeActionColumn: canSeeActionColumn
    })
  }, [user, canSeeActionColumn])

  return (
    <div className="column gap-8 my-organization__health-systems-list">
      <div className="row space-between">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: PLACEHOLDERS.SEARCH,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
        <Can I={ACTIONS.CRUD} a={SUBJECTS.HEALTH_SYSTEM}>
          <Button
            onClick={onAddHS}
            type={BUTTON_TYPES.PRIMARY}
            icon={<Plus16 />}
            upperCase
          >
            {BTN_TXT.ADD_HEALTH_SYSTEM}
          </Button>
        </Can>
      </div>
      {!firstLoad && (
        <Table<THealthSystem>
          dataSource={tableData}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          columns={tableColumns}
          onChangePage={handleChangePageSize}
          onChange={handleTableChange}
        />
      )}
    </div>
  )
}
